import { useEffect, useReducer } from "react";
import { StyledTableBisManualPag } from "@/components/styled/StyledTableBisManualPag";

import { InvoiceUnblockModal } from "./invoiceUnblockModal";
import usePagination from "@/hooks/useSyncedPagination";
import { useGetInvoiceBoV1 } from "@/api/invoice";
import { useBlockedInvoiceTableColumns } from "./blockedInvoiceTableColumns";
import { BlockedInvoicesFilter } from "./blockedInvoicesFilter";
import {
  blockedInvoiceTableReducer,
  initialState,
  createFilterAction,
  createModalAction,
} from "./blockedInvoiceTableReducer";
import { useDisplayDocument } from "@/hooks/useDisplayDocument";
import { InvoicePaymentStatusEnum } from "@/constants/enums/invoicePaymentStatus.enum";
import { InvoiceStatusEnum } from "@/constants/enums/invoiceStatus.enum";

type InvoiceTableProps = {
  companyId?: string;
  billingEntityId?: string | null;
  initMonth?: boolean;
};

export const BlockedInvoiceTable = ({ companyId, billingEntityId }: InvoiceTableProps) => {
  const [state, dispatch] = useReducer(blockedInvoiceTableReducer, {
    ...initialState,
    filters: {
      ...initialState.filters,
    },
  });

  const [pagination, setPagination] = usePagination("invoiceBo");

  const { displayDocument } = useDisplayDocument();

  const { data: invoices } = useGetInvoiceBoV1({
    paymentStatus: InvoicePaymentStatusEnum.TOBEPAID,
    status: [InvoiceStatusEnum.BLOCKED],
    invoiceId: state.filters.searchField ? state.filters.searchField : undefined,
    businessName: state.filters.searchField ? state.filters.searchField : undefined,
    companyId: companyId || undefined,
    billingEntityId: billingEntityId || undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [setPagination, state, companyId, billingEntityId]);

  const columns = useBlockedInvoiceTableColumns(displayDocument, (invoice) =>
    dispatch(createModalAction("selectedInvoice", invoice)),
  );

  return (
    <>
      <div className="flex w-full flex-col items-center justify-between gap-2 xl:flex-row">
        <BlockedInvoicesFilter
          setSearchField={(value) => dispatch(createFilterAction("searchField", value))}
          searchField={state.filters.searchField}
        />
      </div>

      <StyledTableBisManualPag
        data={invoices?.data || []}
        columns={columns}
        setPagination={setPagination}
        pagination={pagination}
        rowCount={invoices?.count || 0}
      />
      <InvoiceUnblockModal
        closeModal={() => dispatch(createModalAction("selectedInvoice", null))}
        invoice={state.modals.selectedInvoice}
      />
    </>
  );
};
